"use client";

import { useSession, signOut } from "@/lib/auth-client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserAvatar, triggerFacehashHover } from "@/components/ui/user-avatar";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Home01Icon,
  Settings02Icon,
  Logout01Icon,
} from "@hugeicons/core-free-icons";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

interface UserNavProps {
  className?: string;
}

export function UserNav({ className }: UserNavProps) {
  const { data: session } = useSession();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const avatarContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLogOut = async () => {
    await signOut({
      fetchOptions: {
        onSuccess: () => {
          router.push("/");
          router.refresh();
        },
      },
    });
  };

  if (!session?.user) {
    return (
      <Link
        href="/sign-up"
        className={cn(
          "relative flex items-center justify-center px-4 py-1.5 bg-gradient-to-r from-[#129990] to-[#1EAD9F] text-white font-normal rounded-sm border border-[#129990]/50 shadow-[inset_0_1px_0px_rgba(255,255,255,0.2),0_0_0_1px_rgba(18,153,144,0.3),0_2px_6px_0_rgba(18,153,144,0.25)] hover:brightness-105 active:translate-y-[2px] transition-all duration-75 text-sm",
          className
        )}
      >
        Get Started
      </Link>
    );
  }

  const name = session.user.name ?? "";
  const email = session.user.email ?? "";
  const displayName = mounted ? name : "";
  const displayEmail = mounted ? email : "";

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onMouseEnter={() => triggerFacehashHover(avatarContainerRef.current, true)}
          onMouseLeave={() => triggerFacehashHover(avatarContainerRef.current, false)}
          className={cn(
            "relative flex items-center justify-center gap-2 px-3 py-1.5 bg-white text-foreground font-normal rounded-sm border border-border hover:bg-muted/40 active:translate-y-[2px] transition-all duration-75 text-sm cursor-pointer outline-none shadow-none",
            className
          )}
        >
          <div ref={avatarContainerRef} className="shrink-0">
            <UserAvatar name={name} email={email} size={22} className="rounded-[6px]" />
          </div>
          <span className="max-w-[140px] truncate font-medium text-foreground">
            {displayName || "Account"}
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-56 rounded-lg" align="end" sideOffset={8}>
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <UserAvatar name={name} email={email} size={32} className="rounded-md shrink-0" />
            <div className="grid flex-1 leading-tight">
              <span className="truncate font-semibold">{displayName || "User"}</span>
              <span className="truncate text-xs text-muted-foreground">{displayEmail}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild className="cursor-pointer">
            <Link href="/dashboard" className="flex items-center w-full gap-2">
              <HugeiconsIcon icon={Home01Icon} size={14} />
              <span>Dashboard</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer" onClick={() => router.push("/dashboard/settings")}>
            <HugeiconsIcon icon={Settings02Icon} size={14} />
            <span>Settings</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogOut} className="text-destructive focus:bg-destructive/10 cursor-pointer">
          <HugeiconsIcon icon={Logout01Icon} size={14} />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
